import clsx from "clsx";
import { Loader2, TriangleAlert } from "lucide-react";
import { type ReactNode, useRef } from "react";

import { type IconProp, renderIcon } from "./ui";
import { t } from "../i18n/i18n";
import { Waiting } from "./Waiting";
import { ReportAnomalyLink } from "../feedback/reportLink";

/** Roue de chargement (R13) : 14 px dans une ligne (`sm`, défaut), 24 px dans un état vide (`lg`) ; grise
 *  (`muted`) ou violette pour l'IA (`run`). Lente en mouvement réduit (index.css). `label` : texte lu par les
 *  lecteurs d'écran (« Chargement… » par défaut) ; pour un texte visible, le poser à côté. */
export function Spinner({
  size = "sm",
  tone = "muted",
  label,
  decorative = false,
  className,
}: {
  size?: "sm" | "lg";
  tone?: "muted" | "run";
  label?: string;
  /** Roue décorative, masquée aux lecteurs d'écran : son conteneur annonce déjà l'état (`EmptyState
   *  variant="loading"`, bouton « Enregistrement… »). */
  decorative?: boolean;
  className?: string;
}) {
  return (
    <Loader2
      size={size === "lg" ? 24 : 14}
      role={decorative ? undefined : "status"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : (label ?? t("Chargement…"))}
      className={clsx("shrink-0 animate-spin", tone === "run" ? "text-run" : "text-ink-muted", className)}
    />
  );
}

/** État vide, chargement ou erreur d'une vue ou d'un panneau (R13), toujours sous son en-tête.
 *
 *  - `page` (défaut) : centré, icône de 24 px grise, `title` en 13 px semi-gras, texte (`children`) en 12 px, une
 *    seule action principale (`action`), une action secondaire facultative (`secondary`, en `Button
 *    variant="link"`), `note` en 11,5 px dessous.
 *  - `inline` : simple note dans la gouttière d'un panneau (`px-4 py-4`, texte gris), action facultative.
 *  - `loading` : roue de 24 px et titre (« Chargement… » par défaut).
 *  - `error` : erreur de chargement, icône d'alerte en `danger`, titre par défaut « Chargement impossible » ; dans
 *    l'espace de travail et l'accueil, lien « Signaler cette anomalie » en dessous (registre F39), avec le texte
 *    affiché.
 *
 *  `compact` : marges de 24 px au lieu de 40 et hauteur à son contenu (carte d'erreur, encart dans un panneau). Le
 *  `className` ne règle que les marges extérieures et la largeur : il ne peut pas surcharger le gabarit (une classe
 *  `py-…` ou `h-…` passée ici perd contre celle du composant, ordre de Tailwind). */
export function EmptyState({
  icon,
  title,
  children,
  action,
  secondary,
  note,
  variant = "page",
  compact = false,
  className,
}: {
  icon?: IconProp;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  secondary?: ReactNode;
  note?: ReactNode;
  variant?: "page" | "inline" | "loading" | "error";
  compact?: boolean;
  /** Marges extérieures et largeur seulement. */
  className?: string;
}) {
  const shown = useRef<HTMLDivElement>(null);
  if (variant === "inline")
    return (
      <div className={clsx("space-y-2 px-4 py-4 text-ui leading-relaxed text-ink-muted", className)}>
        {title && <p className="font-medium text-ink-soft">{title}</p>}
        {children && <div>{children}</div>}
        {(action || secondary) && (
          <div className="flex flex-wrap items-center gap-3">
            {action}
            {secondary}
          </div>
        )}
      </div>
    );
  const glyph =
    variant === "loading" ? (
      <Spinner size="lg" decorative />
    ) : (
      <span className={variant === "error" ? "text-danger" : "text-ink-muted"}>
        {renderIcon(icon ?? (variant === "error" ? TriangleAlert : null), 24)}
      </span>
    );
  const heading = title ?? (variant === "loading" ? <Waiting>{t("Chargement…")}</Waiting> : variant === "error" ? t("Chargement impossible") : null);
  return (
    <div
      ref={shown}
      role={variant === "error" ? "alert" : variant === "loading" ? "status" : undefined}
      className={clsx("flex min-h-0 w-full flex-col items-center justify-center gap-3 px-6 text-center",
        compact ? "py-6" : "h-full py-10", className)}
    >
      {(variant !== "page" || icon !== undefined) && glyph}
      {heading && (
        <h2 className="text-title leading-snug font-semibold text-ink">
          <Waiting>{heading}</Waiting>
        </h2>
      )}
      {children && <div className="max-w-[460px] text-ui leading-relaxed text-ink-soft">{children}</div>}
      {(action || secondary) && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
          {action}
          {secondary}
        </div>
      )}
      {note && <p className="max-w-[460px] text-meta leading-snug text-ink-muted">{note}</p>}
      {variant === "error" && <ReportAnomalyLink source={shown} />}
    </div>
  );
}
